<template>
  <div
    class="bio-content-link-card"
    :class="[
      shapeClass,
      shadowClass,
      {
        'bio-content-link-card-outlined': outlined && !isImageType,
      },
    ]"
    :style="isImageType ? {} : (outlined ? {borderColor: sectionColor, background: backgroundColor} : {background: sectionColor})"
    role="link"
    tabindex="0"
    :aria-label="item.title || item.description || ''"
    @click="$emit('open')"
    @keyup.enter="$emit('open')"
  >
    <SimpleLinkCard
      v-if="!item.type || item.type == 'SAMPLE_LINK'"
      :item="item"
      :sectionColor="sectionColor"
      :sectionTextColor="sectionTextColor"
      :destinationLabel="destinationLabel"
    />
    <ImageLinkCard
      v-else-if="item.type == 'IMAGE'"
      :item="item"
      :destinationLabel="destinationLabel"
      :isImageBroken="isImageBroken"
      :markImageBroken="markImageBroken"
      :sectionColor="sectionColor"
      :sectionTextColor="sectionTextColor"
    />
    <YoutubeLinkCard
      v-else-if="item.type == 'YOUTUBE'"
      :item="item"
      :sectionTextColor="sectionTextColor"
      :convertToEmbedUrl="convertToEmbedUrl"
    />
    <WhatsappLinkCard
      v-else-if="item.type == 'WHATSAPP'"
      :item="item"
      :sectionTextColor="sectionTextColor"
      :destinationLabel="destinationLabel"
    />
    <InstagramLinkCard
      v-else-if="item.type == 'INSTAGRAM'"
      :item="item"
      :sectionTextColor="sectionTextColor"
      :isImageBroken="isImageBroken"
      :markImageBroken="markImageBroken"
    />
  </div>
</template>

<script>
import { gsap } from 'gsap'
import SimpleLinkCard from './SimpleLinkCard.vue'
import ImageLinkCard from './ImageLinkCard.vue'
import YoutubeLinkCard from './YoutubeLinkCard.vue'
import WhatsappLinkCard from './WhatsappLinkCard.vue'
import InstagramLinkCard from './InstagramLinkCard.vue'

export default {
  name: 'BioContentLinkRenderer',
  components: { SimpleLinkCard, ImageLinkCard, YoutubeLinkCard, WhatsappLinkCard, InstagramLinkCard },
  props: {
    item: { type: Object, required: true },
    index: { type: Number, default: 0 },
    shapeClass: { type: String, default: 'rounded' },
    outlined: { type: Boolean, default: false },
    shadowClass: { type: String, default: '' },
    sectionColor: { type: String, default: '#101827' },
    sectionTextColor: { type: String, default: '#FFFFFF' },
    backgroundColor: { type: String, default: '#FFFFFF' },
    destinationLabel: { type: String, default: null },
    isImageBroken: { type: Function, required: true },
    markImageBroken: { type: Function, required: true },
    convertToEmbedUrl: { type: Function, required: true },
  },
  computed: {
    isImageType: function() {
      return this.item.type === 'IMAGE'
    },
  },
  mounted: function() {
    // Reveal-on-scroll (not just on mount) - most content links start
    // below the fold, especially on desktop where .bio-content-col
    // scrolls internally rather than the page. IntersectionObserver
    // (root:null) tracks true on-screen position regardless of which
    // ancestor actually scrolls - same reasoning as the sticky header's
    // visibility fix earlier, and avoids needing GSAP's ScrollTrigger to
    // be told which element is the scroller (this site has two different
    // scroll containers depending on breakpoint).
    const prefersReducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    const el = this.$el
    if (prefersReducedMotion) {
      gsap.set(el, { opacity: 1, y: 0 })
      return
    }
    gsap.set(el, { opacity: 0, y: 20 })
    const delay = Math.min(this.index, 8) * 0.06
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return
      // clearProps removes GSAP's own inline transform once the tween
      // finishes, so the CSS :hover/:active transform rules below regain
      // control instead of being permanently shadowed by an inline style.
      gsap.to(el, { opacity: 1, y: 0, duration: 0.5, delay, ease: 'power2.out', clearProps: 'transform' })
      observer.disconnect()
    }, { threshold: 0.15 })
    observer.observe(el)
    this._revealObserver = observer
  },
  beforeDestroy: function() {
    if (this._revealObserver) this._revealObserver.disconnect()
  },
}
</script>

<style scoped>
.bio-content-link-card {
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.bio-content-link-card:focus-visible {
  outline: 2px solid #0277BD;
  outline-offset: 2px;
}
.bio-content-link-card-outlined {
  border: 1px solid;
}
/* Image links skip the colored border/fill other types use (the photo
   speaks for itself). They used to get a standing box-shadow instead so
   they'd still read as distinct at rest, but per feedback the elevation
   should only appear on hover (see the shared :hover rule below) rather
   than being a permanently-defined shadow. */
.bio-content-link-card:active {
  transform: scale(0.98);
}
@media (hover: hover) {
  .bio-content-link-card:hover {
    /* Stronger than before (-2px / 0.14) - the standing shadow image
       links used to carry is gone now (see 13.20), so this is the only
       elevation feedback left and it needs to read clearly on its own,
       including against a shadowClass like sombra-interna's inset shadow
       at rest, per feedback that the lift wasn't noticeable enough. */
    transform: translateY(-4px);
    box-shadow: 0 14px 28px rgba(16, 24, 39, 0.2);
  }
}
@media (prefers-reduced-motion: reduce) {
  .bio-content-link-card {
    transition: none;
  }
  .bio-content-link-card:hover {
    transform: none;
    box-shadow: none;
  }
}
</style>
